// 15章 左官工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_MORTAR_LAYERS = `
<svg viewBox="0 0 660 260" role="img" aria-label="モルタル塗りの層構成。下地に吸水調整材を塗り、下塗り、むら直し、中塗り、上塗りの順に塗り重ねる。1回の塗厚は7ミリ以下、全塗厚は25ミリ以下。下塗りは14日以上放置してひび割れを出してから次を塗る">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="60" width="26" height="150"/></g>
  <g fill="currentColor" opacity="0.5"><rect x="86" y="60" width="18" height="150"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="104" y="60" width="14" height="150"/></g>
  <g fill="currentColor" opacity="0.6"><rect x="118" y="60" width="14" height="150"/></g>
  <g fill="currentColor" opacity="0.75"><rect x="132" y="60" width="12" height="150"/></g>
  <g stroke="currentColor" stroke-width="1.2" opacity="0.6">
    <line x1="86" y1="60" x2="86" y2="210"/><line x1="104" y1="60" x2="104" y2="210"/>
    <line x1="118" y1="60" x2="118" y2="210"/><line x1="132" y1="60" x2="132" y2="210"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="160" y="80">① 下地（コンクリート等）＋吸水調整材</text>
    <text x="160" y="106">② 下塗り（金ぐしで荒らし目→14日以上放置）</text>
    <text x="160" y="132">③ むら直し（むらが著しい場合）</text>
    <text x="160" y="158">④ 中塗り（定規通しよく平らに）</text>
    <text x="160" y="184">⑤ 上塗り（金ごて／木ごて／はけ引き）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="238">1回の塗厚は7mm以下、全塗厚（タイル張付けモルタル含む）は25mm以下。下塗りは14日以上放置が原則</text>
  </g>
</svg>`;

const SVG_FINISH_TROWEL = `
<svg viewBox="0 0 660 200" role="img" aria-label="上塗り仕上げの3種類。金ごて仕上げは塗装下地・防水下地・タイル接着剤張り下地。木ごて仕上げはセメントモルタル張りタイル下地。はけ引き仕上げは粗面に仕上げる">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20" y="50" width="200" height="120" rx="10"/>
    <rect x="230" y="50" width="200" height="120" rx="10"/>
    <rect x="440" y="50" width="200" height="120" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="120" y="80">金ごて仕上げ</text>
    <text x="330" y="80">木ごて仕上げ</text>
    <text x="540" y="80">はけ引き仕上げ</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="120" y="108">つるつる・平滑</text>
    <text x="120" y="132">塗装・壁紙・防水</text>
    <text x="120" y="150">タイル接着剤張り下地</text>
    <text x="330" y="108">少しざらつき</text>
    <text x="330" y="132">セメントモルタル張り</text>
    <text x="330" y="150">タイル下地</text>
    <text x="540" y="108">はけ目・粗面</text>
    <text x="540" y="132">吹付け等の下地</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="40">「次に何を載せるか」で上塗りの粗さを変える（つるつる過ぎると次の材料が付かない）（15.3.5）</text>
  </g>
</svg>`;

const SVG_SL = `
<svg viewBox="0 0 660 220" role="img" aria-label="セルフレベリング材塗り。流動性の高い材料をレベルに合わせて流し込むと、自らの重さで水平に広がり平滑な床下地になる。標準塗厚10ミリ程度、養生7日以上、表面仕上材までは30日以内">
  <g fill="currentColor" opacity="0.25"><rect x="80" y="150" width="500" height="40"/></g>
  <g fill="currentColor" opacity="0.55"><rect x="80" y="130" width="500" height="20"/></g>
  <g stroke="currentColor" stroke-width="1.5" stroke-dasharray="6 4" opacity="0.8">
    <line x1="80" y1="130" x2="580" y2="130"/>
  </g>
  <g fill="currentColor" opacity="0.7">
    <path d="M300 60 q10 30 0 60 q-10 -30 0 -60" />
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="80" y="45" font-weight="bold">セルフレベリング材（自己水平性）</text>
    <text x="320" y="95">流し込むと自らの重さで水平に広がる</text>
    <text x="90" y="145" opacity="0.85">→ こてで均さなくても平滑な床下地に</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="80" y="212">標準塗厚10mm程度。養生7日以上（低温14日以上）、表面仕上材の施工まで30日以内（15.5）</text>
  </g>
</svg>`;

const SVG_SHIAGETO = `
<svg viewBox="0 0 660 240" role="img" aria-label="仕上塗材の種類。薄付け仕上塗材は砂壁状・ゆず肌状で吹付けやローラー、厚付け仕上塗材はスタッコ状、複層仕上塗材は下塗材・主材・上塗材の3層で吹付けタイル状。JIS A 6909による">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20" y="60" width="200" height="130" rx="10"/>
    <rect x="230" y="60" width="200" height="130" rx="10"/>
    <rect x="440" y="60" width="200" height="130" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="120" y="88">薄付け仕上塗材</text>
    <text x="330" y="88">厚付け仕上塗材</text>
    <text x="540" y="88">複層仕上塗材</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="120" y="114">砂壁状・ゆず肌状</text>
    <text x="120" y="136">（リシン等）</text>
    <text x="120" y="162">下塗材＋主材</text>
    <text x="330" y="114">スタッコ状・凹凸大</text>
    <text x="330" y="136">（吹放し等）</text>
    <text x="330" y="162">下塗材＋主材</text>
    <text x="540" y="114">吹付けタイル状</text>
    <text x="540" y="136">下塗材＋主材＋上塗材</text>
    <text x="540" y="162">の3層構成</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="220">JIS A 6909による。下塗材・主材・上塗材は同一製造所の製品。内装はF☆☆☆☆（15.6）</text>
  </g>
</svg>`;

const SVG_ROCKWOOL = `
<svg viewBox="0 0 640 200" role="img" aria-label="ロックウール吹付け。ロックウール60から85パーセントとセメント15から40パーセントを混合し、密度0.2グラム毎立方センチ以上で吹き付ける。吹付け厚さは所定の1.2倍とし、こてで圧縮して所定厚に仕上げる">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="90" width="360" height="40"/></g>
  <g fill="currentColor" opacity="0.55">
    <circle cx="450" cy="70" r="4"/><circle cx="470" cy="90" r="5"/><circle cx="440" cy="100" r="4"/>
    <circle cx="480" cy="110" r="4"/><circle cx="460" cy="120" r="5"/>
  </g>
  <g stroke="currentColor" stroke-width="3"><line x1="540" y1="60" x2="490" y2="95"/></g>
  <g fill="currentColor" font-size="12.5">
    <text x="60" y="45" font-weight="bold">ロックウール吹付け（不燃・耐火被覆・吸音）</text>
    <text x="550" y="60">吹付けガン</text>
    <text x="70" y="115" opacity="0.85">下地</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="165">配合：ロックウール60〜85%＋セメント15〜40%、密度0.2g/cm³以上。厚さは所定の1.2倍→こて圧縮</text>
    <text x="60" y="186" opacity="0.85">鉄骨の耐火被覆は7章9節による。吹付け後7日程度自然乾燥（15.12）</text>
  </g>
</svg>`;

const ch15: Record<string, Commentary> = {
  "15.1.1": {
    plainSummary:
      "この章はモルタル塗り・床コンクリート直均し・セルフレベリング材塗り・仕上塗材・各種プラスター・しっくい・こまい壁・ロックウール吹付けの左官工事に適用する。1章とセットで適用する。",
    why: "左官は「こてで塗る」仕事全般です。下地を平らにするモルタル塗り、床を平滑にする直均し・SL材、意匠を作る仕上塗材やしっくい、耐火・吸音のロックウール吹付けまで幅広い。共通するのは「乾燥・ひび割れ・接着」の管理です。",
  },

  "15.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②所定の塗厚で所要の状態の仕上り面 ③塗り付けた材料に有害な浮きがないこと。",
  },

  "15.1.3": {
    plainSummary: "施工に先立ち、見本帳または見本塗板を監督職員に提出する。",
  },

  "15.1.4": {
    plainSummary:
      "近接部材・他の仕上げ面を汚さないよう養生する。塗り面の汚れと急激な乾燥を防ぐためシート掛け・水湿しを行う。気温5℃以下は施工しない（やむを得ず施工する場合は板覆い・シート掛け＋ヒーター採暖）。",
    why: "左官材料は水で硬化するため、急激に乾くと硬化不良・ひび割れになります。だから「乾かさない養生」が重要。5℃以下は凍結や硬化不良のリスクで原則施工しない、というのはタイル・塗装とも共通の寒中施工の基本です。",
    points: [
      "急激な乾燥を防ぐ（水湿し・シート掛け）",
      "5℃以下は原則施工しない",
    ],
  },

  "15.1.5": {
    plainSummary:
      "打継ぎ部・開口部回り・ラスボード継目などひび割れのおそれのある箇所は、モルタルの場合メタルラス張り、プラスターの場合しゅろ毛・ガラス繊維ネット等を伏せ込む。下地が異なる取合い・ひび割れ誘発目地には目地・見切り縁を設ける。",
    why: "左官仕上げの最大の弱点はひび割れ。動きや下地の違いが集中する箇所（開口の角、下地の継目）は必ずひび割れるので、ラスや繊維で補強するか、あらかじめ目地を切って「割れる場所を決めておく」のが対策です。",
    glossary: [
      {
        term: "メタルラス",
        meaning: "金網状の左官下地材。モルタルの食い込みと補強を兼ねる。",
      },
      {
        term: "見切り縁",
        meaning: "仕上げの端部や下地の変わり目に入れる細い部材。ひび割れを目立たせない。",
      },
    ],
  },

  "15.1.6": {
    plainSummary:
      "コンクリートの打放し仕上げ・平たんさの種別は6.2.5（構造体コンクリートの仕上り）による。",
  },

  "15.2.1": {
    plainSummary:
      "この節は床直均し・SL材・ロックウール吹付けを除く左官工事の下地・下地工法について適用する。",
  },

  "15.2.2": {
    plainSummary:
      "下地は左官塗りの種類に化学的に適合した材質とし、錆・化学反応・吸水による塗り層の脆弱化が生じないものとする。",
    why: "左官材料と下地の相性は重要。例えばアルカリ性のモルタルと反応する材料を下地にすると、後で膨れや剥離が起こります。",
  },

  "15.2.3": {
    plainSummary:
      "コンクリート系下地は、型枠を完全撤去しはく離剤等の残存物がないこと、ひび割れ・豆板・過度の凹凸がない（または補修済み）こと、はく離防止の目荒し・ぜい弱層除去・清掃が行われていること。",
    points: [
      "はく離剤の残りは接着不良の原因。目荒し・清掃で除去",
    ],
  },

  "15.2.4": {
    plainSummary:
      "ラス系下地は木下地の内外壁に適用。通気構法は二層下地/単層下地、直張りはラスモルタル/ラスシートモルタル下地。ラスはJIS A 5505、ステープルはステンレス製。防水紙・透湿防水シートを併用する。",
    glossary: [
      {
        term: "ラス下地",
        meaning:
          "木下地に金網（ラス）を張ってモルタルを塗る下地。ラスがモルタルを保持・補強する。",
      },
    ],
  },

  "15.2.5": {
    plainSummary:
      "せっこうボード・せっこうラスボード・木質系セメント板で施工する場合の下地。ボードはJIS A 6901、木質系セメント板はJIS A 5404、留付け小ねじは19.7.2による。",
  },

  "15.2.6": {
    plainSummary:
      "こまい下地は木下地の内外壁に壁土塗りを行う下地。真竹の割竹（間渡し竹・こまい竹）を掻き縄で組み、柱に穴を彫って間渡し竹を渡す伝統工法。",
    glossary: [
      {
        term: "こまい（小舞）",
        meaning:
          "竹や木を格子状に編んだ土壁の下地。この上に荒壁土を塗り重ねる。",
      },
    ],
  },

  "15.2.7": {
    plainSummary:
      "木ずり下地は木下地の内外壁にプラスター・しっくいを塗る場合の下地。厚さ7mm・幅40mmの小幅板を柱・間柱に直角に配置し目透しをとって釘打ちする。",
    glossary: [
      {
        term: "木ずり",
        meaning:
          "細い板を隙間（目透し）をあけて張った左官下地。塗り材が隙間に食い込んで保持される。",
      },
    ],
  },

  "15.3.1": {
    plainSummary:
      "この節はコンクリート下地等に施すセメント・細骨材を主材料としたモルタル塗りに適用する。",
  },

  "15.3.2": {
    plainSummary:
      "モルタルは現場調合または既調合。砂は塩分・泥土・有機物を有害量含まず表15.3.1の粒度。混和材（消石灰等）・保水剤・ポリマーディスパージョン・吸水調整材を使う。吸水調整材は表15.3.2の品質。",
    glossary: [
      {
        term: "吸水調整材",
        meaning:
          "下地に塗って過度の吸水を抑え、モルタルのドライアウト（水分が下地に吸われて硬化不良）を防ぐ材料。",
      },
    ],
  },

  "15.3.3": {
    plainSummary:
      "調合・塗厚は表15.3.3（内壁は下塗り1:2.5・中塗り上塗り1:3で塗厚20mm等）。防水下地の床・立上りは15mm以上。1回の塗厚は7mm以下（床除く）。仕上げ厚・全塗厚（タイル張付けモルタル含む）は25mm以下（床除く）。練混ぜは原則機械練り。",
    why: "1回7mm以下・全体25mm以下は左官の超重要数値。厚く塗ると自重で垂れ、乾燥収縮でひび割れます。だから薄く塗り重ねるのが原則です。",
    points: [
      "1回の塗厚は7mm以下、全塗厚は25mm以下（床除く）",
      "厚塗りはひび割れ・剥落の原因",
    ],
    diagrams: [
      {
        svg: SVG_MORTAR_LAYERS,
        caption: "モルタル塗りの層構成と塗厚（15.3.3）",
      },
    ],
  },

  "15.3.4": {
    plainSummary:
      "コンクリート壁・床でひずみ・不陸の著しい箇所は目荒し・水洗い後にモルタル等で補修し14日以上放置。壁面は水洗いで接着阻害物を除去。コンクリート床は硬化後可能な限り早期に塗付け。高圧水洗の目荒しは試験施工して承諾を受ける。",
    why: "下地補修後の「14日以上放置」は、補修モルタルが乾燥収縮しひび割れを出し切ってから次を塗るため。急ぐとあとから割れます。床を早期に塗るのは、コンクリートが古くなるほど接着が悪くなるからです。",
  },

  "15.3.5": {
    plainSummary:
      "壁塗りは下塗り→むら直し→中塗り→上塗りの順。下塗りは吸水調整材塗布後に全面塗り、金ぐしで荒らし目、14日以上放置。中塗りは定規通しよく平らに。上塗りは仕上げの種類（金ごて/木ごて/はけ引き）で仕上げる。仕上げの種類は施工箇所に応じる（塗装・防水下地は金ごて、タイル張り下地は木ごて）。",
    why: "左官の腕の見せ所。下塗りに荒らし目をつけるのは、次の層が食いつくため（つるつるだと剥がれる）。上塗りの仕上げは「次に何を載せるか」で決めます。タイルを張るなら少しざらつく木ごて、塗装するなら平滑な金ごて、という具合です。",
    points: [
      "下塗り→むら直し→中塗り→上塗りの順",
      "下塗りに荒らし目（食いつきのため）＋14日以上放置",
      "上塗りの仕上げは次工程で決める（金ごて/木ごて/はけ引き）",
    ],
    glossary: [
      {
        term: "ちり回り",
        meaning: "柱・枠と壁面が接する見切り部分。定規塗りで正確に納める。",
      },
    ],
    diagrams: [
      {
        svg: SVG_FINISH_TROWEL,
        caption: "上塗り仕上げの3種類（15.3.5）",
      },
    ],
  },

  "15.4.1": {
    plainSummary:
      "この節は床コンクリート打込み後の表面を金ごて仕上げ・粗面仕上げとする工事、塗物・敷物・張物の下地・防水下地の工事に適用する。",
  },

  "15.4.2": {
    plainSummary:
      "床面の仕上りの平たんさは、幅木の張付け方向で3mにつき3mm以内、むらは目視で支障がない程度、それ以外は6.2.5による。",
  },

  "15.4.3": {
    plainSummary:
      "6.6.6（上面の仕上げ）後、中むら取りを木ごて→踏板で金ごて押えしセメントペーストを浮き出させる→締り具合を見て金ごてで強く押さえ平滑にする。粗面仕上げはデッキブラシで目通りよく。薄い仕上げは下ずり・中ずり・仕上げずりの3工程。SL材の場合は金ごて押え1回。",
    why: "床の直均しは「セメントペーストを表面に浮き出させて金ごてで締める」のがコツ。これで硬く緻密で平滑な床になります。ビニル床など薄い仕上げほど下地の凸凹が響くので、3工程で丁寧に仕上げます。",
    glossary: [
      {
        term: "直均し（じかならし）",
        meaning:
          "床コンクリートの表面をそのまま金ごてで平滑に仕上げること。モルタルを別に塗らない。",
      },
    ],
  },

  "15.4.4": {
    plainSummary:
      "表面仕上げ後、硬化状態を見計らい6章7節（養生）によるほかビニルシート等で表面保護。上階の型枠取付け・鉄筋運搬時に仕上げ面を傷つけないようにする。",
  },

  "15.5.1": {
    plainSummary: "この節は内装の張物下地のセルフレベリング材塗りに適用する。",
    glossary: [
      {
        term: "セルフレベリング材（SL材）",
        meaning:
          "流動性が高く、流し込むと自らの重さで水平に広がる床下地材。こて均し不要で平滑な床が得られる。",
      },
    ],
  },

  "15.5.2": {
    plainSummary:
      "SL材はせっこう系・セメント系（表15.5.1、フロー値19cm以上等の品質）で施工箇所に適したもの。吸水調整材の品質は表15.3.2による。",
  },

  "15.5.3": {
    plainSummary:
      "調合はSL材の製造所仕様。標準塗厚は10mm程度。",
  },

  "15.5.4": {
    plainSummary:
      "下地コンクリート床は金ごて押え。乾燥を見計らい清掃し接着阻害物を除去。デッキブラシで吸水調整材塗り2回を標準として乾燥させる。",
    why: "SL材は水のように流れるので、下地に穴や隙間があると流れ出てしまいます。吸水調整材を塗るのは、下地が水を吸ってSL材が硬化不良を起こすのを防ぐためです。",
  },

  "15.5.5": {
    plainSummary:
      "機械練りしレベルに合わせて流し込む。気温5℃以下は施工しない。硬化後の打継ぎ部の突起はサンダーで削り、気泡跡はSL材の指定材料で補修。養生は硬化まで窓・開口部をふさぎ、7日以上（低温14日以上）、表面仕上材の施工まで30日以内。",
    points: [
      "流し込むだけで水平（こて均し最小限）",
      "養生7日以上、仕上材施工まで30日以内",
      "硬化後の突起・気泡跡は削り・補修",
    ],
    diagrams: [
      {
        svg: SVG_SL,
        caption: "セルフレベリング材塗り（15.5）",
      },
    ],
  },

  "15.6.1": {
    plainSummary: "この節は建築用仕上塗材を用いる内外装の仕上工事に適用する。",
    glossary: [
      {
        term: "仕上塗材",
        meaning:
          "吹付けやローラー・こてで模様をつける仕上げ材（リシン・スタッコ・吹付けタイル等）。JIS A 6909による。",
      },
    ],
  },

  "15.6.2": {
    plainSummary:
      "仕上塗材はJIS A 6909による（内装はF☆☆☆☆）。種類（呼び名）・仕上げの形状・工法は特記。指定色・つやは製造所で調合、有効期間切れは使用しない。下塗材・主材・上塗材は同一製造所の製品。下地調整塗材はJIS A 6916。",
    diagrams: [
      {
        svg: SVG_SHIAGETO,
        caption: "仕上塗材の種類（15.6.2）",
      },
    ],
  },

  "15.6.3": {
    plainSummary:
      "見本塗板は所要量・塗厚が工程ごとに確認できるもの。外部の仕上げ塗りは結露のおそれ・強風時は原則行わない。溶剤使用時は換気。工程ごとの所要量を15.6.7で確認。シーリング面への仕上塗材は硬化後に行い塗重ね適合性を確認。",
    points: [
      "所要量（kg/m²）で塗厚を管理する",
      "シーリング上への塗装は硬化後＋適合性確認",
    ],
  },

  "15.6.4": {
    plainSummary:
      "モルタル・プラスター下地のひび割れはU字にはつってモルタル充填し14日程度放置。外壁コンクリート下地のひび割れ処理は協議。モルタル下地の仕上げは仕上塗材の種類に応じた仕上げ（表15.6.3）。ALCパネル・押出成形セメント板は製造所指定の補修材で平滑に。",
  },

  "15.6.5": {
    plainSummary:
      "コンクリート面は目違いをサンダーで除去・清掃後、下地調整塗材C-2を1〜2mm全面塗り平滑に（不陸1mm以下はC-1、3〜10mmはCM-2）。モルタル・PC面は合成樹脂エマルションシーラー。ボード面はB種＋シーラー。ALCは清掃＋シーラー。",
    why: "仕上塗材はごく薄いので下地の凸凹がそのまま出ます。下地調整塗材で平滑にしておくのが美しい仕上げの前提。不陸の大きさで使う材料（C-1/C-2/CM-2）を使い分けます。",
  },

  "15.6.6": {
    plainSummary:
      "各仕上塗材（外装薄塗材Si/E/S、防水形、内装薄塗材C/L/Si/E等）ごとに、練混ぜ→下塗り→主材塗り（吹付け/ローラー/こて）の工法が定められている。見本と同様の模様に均一に仕上げる。",
    why: "仕上塗材は模様が命。吹付けの空気圧やローラーの種類で模様が変わるため、必ず見本と同じ条件で施工します。防水形は増塗り（弱点部の先塗り）で下地のひび割れ追従性を確保します。",
  },

  "15.6.7": {
    plainSummary:
      "所要量の確認は防水形・軽量骨材仕上塗材の場合は単位面積当たりの使用量による。仕上りの程度は見本との比較・むらやはじきの有無で確認する（表15.6.4）。",
  },

  "15.7.1": {
    plainSummary:
      "この節はコンクリート面・押出成形セメント板面・モルタル面・ALCパネル面へのマスチック塗材塗りに適用する。",
    glossary: [
      {
        term: "マスチック塗材",
        meaning:
          "多孔質ローラーで一段塗りで凹凸模様をつける厚膜の塗材。工程が少なく経済的。",
      },
    ],
  },

  "15.7.2": {
    plainSummary:
      "マスチック塗材塗りは素地ごしらえ→下地押え（シーラー）→塗材塗り（マスチック塗材A 1.20kg/m²）。多孔質ハンドローラーでくばり塗り→均し塗り→ローラー転圧でパターン付けし一段塗りで仕上げる。塗継ぎ幅は800mm程度。",
  },

  "15.8.1": {
    plainSummary: "この節はせっこうプラスター塗りに適用する。",
    glossary: [
      {
        term: "せっこうプラスター",
        meaning:
          "せっこうを主材とした塗り壁材。硬化が速く収縮が少ない。内装に使う。",
      },
    ],
  },

  "15.8.2": {
    plainSummary:
      "せっこうプラスターはJIS A 6904。下塗り・中塗りは既調合または現場調合プラスター（下塗り用）。製造後4か月以上経過したものは使用しない。",
    why: "せっこうは湿気を吸うと固まる性質があり、古い材料は袋の中で劣化しています。「4か月以上経過品は使わない」という賞味期限があります。",
  },

  "15.8.3": {
    plainSummary:
      "壁の工程・塗厚は表15.8.1（下塗り6〜8mm・中塗り5〜7mm・上塗り3〜5mm等）。○印の工程を行う。",
  },

  "15.8.4": {
    plainSummary: "下地処理は15.3.4による。",
  },

  "15.8.5": {
    plainSummary:
      "下地モルタル塗り（コンクリートはシーラー後1:3モルタル6mm、ラスはラス付け後6mm、14日以上放置）。せっこうプラスターは加水後可使時間以内に施工。下塗りはくし目をつける。開口部周辺等はしゅろ毛等を伏せ込む。中塗りは定木づり。上塗りは下付け・上付けの2工程で仕上げこてで平滑に。",
    points: [
      "加水後は可使時間以内に使い切る（せっこうは硬化が速い）",
      "ひび割れのおそれのある箇所はしゅろ毛等で補強",
    ],
  },

  "15.8.6": {
    plainSummary:
      "塗り作業中は通風をなくし、硬化まで通風を避け、その後は適度な通風で乾燥させる。気温が低い場合は施工しない（やむを得ず施工する場合は採暖）。",
    why: "せっこうは硬化前に風を当てると表面だけ先に乾いてしまい、内部が固まらず強度不足になります。だから「硬化まで通風を避ける」のがプラスターの養生の特徴です。",
  },

  "15.9.1": {
    plainSummary: "この節はドロマイトプラスター塗りに適用する。",
    glossary: [
      {
        term: "ドロマイトプラスター",
        meaning:
          "ドロマイト（苦灰石）を焼いた消石灰系の塗り壁材。粘りがあり塗りやすいが乾燥収縮でひび割れやすいためすさを混ぜる。",
      },
    ],
  },

  "15.9.2": {
    plainSummary:
      "ドロマイトプラスターはJIS A 6903。すさ（さらしすさ・白毛すさ）、下げお、しゅろ毛・パーム、セメント、砂、水を用いる。",
    glossary: [
      {
        term: "すさ",
        meaning:
          "塗り壁に混ぜる繊維（わら・麻・紙等）。ひび割れを防ぎ、塗り材のつなぎになる。",
      },
    ],
  },

  "15.9.3": {
    plainSummary:
      "調合・塗厚は表15.9.1。天井・ひさしの総塗厚は12mm以下。下塗り・むら直し・中塗り・上塗りにすさを混ぜる。",
  },

  "15.9.4": {
    plainSummary:
      "下地モルタル塗りは15.8.5(1)。下げお打ちはちり回り用を150mm以下の間隔で1列に。すさは乾燥・たたきほぐし、均一分散するよう練る。上塗り用は水と練って12時間程度おいてから使う。下塗り・むら直し・中塗り・上塗りの順。",
  },

  "15.9.5": {
    plainSummary:
      "塗り作業中は通風をなくし、各層後は徐々に換気して乾燥。気温が低い場合は施工しない（やむを得ず施工する場合は採暖）。",
  },

  "15.10.1": {
    plainSummary:
      "この節は消石灰・砂・のり・すさを主材料としたしっくいを内外壁に塗り付けるしっくい塗りに適用する。下地はボード・モルタル・木ずり・こまい土壁等。",
    glossary: [
      {
        term: "しっくい（漆喰）",
        meaning:
          "消石灰にのり・すさ・砂を加えた伝統的な塗り壁材。調湿・防火性に優れ、白く美しい。",
      },
    ],
  },

  "15.10.2": {
    plainSummary:
      "しっくいは既調合（内装上塗用はJIS A 6919）または現場調合（消石灰・貝灰・すさ・のり）。のりはつのまた・ぎんなんそう等。",
  },

  "15.10.3": {
    plainSummary:
      "既調合しっくいの調合・塗厚は特記がなければ下地別に表15.10.1〜（ボード下地は下塗り1〜3mm・上塗り1〜2mm等）。現場調合は木ずり下地・せっこうプラスター下地・こまい土壁下地で調合が異なる。",
  },

  "15.10.4": {
    plainSummary:
      "しっくいは施工前日までに練り混ぜ、乾燥しないよう覆い、施工直前に再練りして使う。既調合はボード下地の継目処理（ジョイントコンパウンド＋テープ）後、シーラー塗布・下塗り・上塗り。現場調合はのり・すさ・石灰・砂を熱いうちに練り混ぜる。上塗りは押え仕上げ・なで切り仕上げ・パターン仕上げから特記による。",
  },

  "15.10.5": {
    plainSummary:
      "塗り作業中は通風をなくし、水引き具合を見て徐々に通風、その後自然乾燥。5℃を下回る日は施工しない。施工中・後は雨・油煙を避ける。日射・強風の屋外はシート養生＋下地に散水養生。",
    why: "しっくいは消石灰が空気中の二酸化炭素とゆっくり反応して固まります（気硬性）。だから急激な乾燥は大敵で、雨・直射日光・強風を避けてゆっくり硬化させます。",
  },

  "15.11.1": {
    plainSummary:
      "この節はこまい下地に壁土で荒壁から中塗りまで施工し、色土・色砂・消石灰入り壁土で上塗りする土物壁・砂壁・大津壁に適用する。",
    glossary: [
      {
        term: "土物壁",
        meaning: "色土を主材にした塗り壁。じゅらく壁などの伝統的な和風壁。",
      },
      {
        term: "大津壁",
        meaning: "色土に消石灰を混ぜて磨き上げる高級な塗り壁仕上げ。",
      },
    ],
  },

  "15.11.2": {
    plainSummary:
      "荒壁土（砂質粘土、15mmふるい通過）・中塗り土（10mmふるい通過）、わらすさ（きりわら・もみすさ・みじんすさ）・紙すさ、のり、色土、消石灰、色砂等を用いる。",
  },

  "15.11.3": {
    plainSummary:
      "下塗り・むら直し・中塗り・上塗りの調合は特記がなければ表15.11.2〜（荒壁土＋わらすさ等）。土物仕上げ・砂壁仕上げ・切返し仕上げで調合が異なる。",
  },

  "15.11.4": {
    plainSummary:
      "塗厚は特記がなければ表15.11.8（両面仕上げ計70〜81mm、片面仕上げ56〜63mm）。柱ちりを残して上塗り面を標準とする。",
  },

  "15.11.5": {
    plainSummary:
      "こまい壁の工程は表15.11.9（荒壁塗り→裏なで→裏返し→墨打ち→貫伏せ→荒付け→ちり回り→中塗り→上塗り）で特記がなければA種。土物仕上げは水ごね・のりさし・のりごね等の工程。",
  },

  "15.11.6": {
    plainSummary:
      "下塗りは荒壁土をこまいにすり込み厚さ12mm程度塗り、裏なで（同日中にこてでなで返す）・裏返し。むら直しは墨打ち・貫伏せ（貫材と荒壁を連結）・ちり回り・むら直し塗り。中塗りは十分乾燥後にちり回りを正しく平滑にこて押え。",
  },

  "15.11.7": {
    plainSummary:
      "土物仕上げは水ごね土物1/2工法・のりさし土物工法・のりごね土物工法・砂壁仕上げ・切返し仕上げから特記による。地むら・こてむら・ちりぎれがないよう入念に塗り仕上げる。ちりじゃくりは上塗り面をこて刃先の厚さ（1mm程度）透かせて納める。",
  },

  "15.11.8": {
    plainSummary:
      "大津仕上げは普通大津仕上げ工法・大津みがき仕上げ工法から特記による。大津みがきは下付け・上付けを同日中に仕上げ、こてで磨き、つやが出始めたら少量の水を含ませた布でふき、磨きを数回繰り返す。",
  },

  "15.12.1": {
    plainSummary:
      "この節は半乾式工法・乾式工法によるロックウール吹付けに適用する。鉄骨工事の耐火被覆は7章9節による。",
    glossary: [
      {
        term: "ロックウール吹付け",
        meaning:
          "鉱物繊維をセメントと混ぜて吹き付ける。不燃・耐火被覆・吸音・断熱に使う。",
      },
    ],
  },

  "15.12.2": {
    plainSummary:
      "ロックウールはJIS A 9504かつ不燃材料（F☆☆☆☆）。セメントはポルトランド・高炉・白セメント。接着剤は合成樹脂系（F☆☆☆☆）。",
  },

  "15.12.3": {
    plainSummary:
      "配合はロックウール60〜85%＋セメント15〜40%、密度0.2g/cm³以上で防火材料に適用できるもの。仕上げ吹付け厚さは特記。",
  },

  "15.12.4": {
    plainSummary:
      "下地処理（鉄骨は浮き錆・油を除去、モルタル下地は木ごて仕上げ）。適切な機器で混合吹付けし必要な厚さ・密度に付着。吹付け厚さは所定の1.2倍としこてで圧縮して所定厚に仕上げる。厚さ50mm以上ではく離のおそれがあれば接着剤か剥離防止金物。吹付け後7日程度自然乾燥。",
    points: [
      "厚さは所定の1.2倍→こて圧縮で所定厚に",
      "厚さ50mm以上は剥離防止（接着剤・金物）",
    ],
    diagrams: [
      {
        svg: SVG_ROCKWOOL,
        caption: "ロックウール吹付け（15.12）",
      },
    ],
  },
};

export default ch15;
